import { useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
// material
import { styled, useTheme } from '@mui/material/styles';
// hooks
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
import useAuth from '../../hooks/useAuth';
import { userRoles } from '../../constants/defaultValues';
import { peekOnboardTeamSite } from '../../services/onboardingTeam';
//
import DashboardNavbar from './DashboardNavbar';
import DashboardSidebar from './DashboardSidebar';
import DashboardFooter from './DashboardFooter';
import ImpersonationBanner, { BANNER_HEIGHT } from '../../components/ImpersonationBanner';
import ReadOnlyBanner from '../../components/ReadOnlyBanner';

// ----------------------------------------------------------------------

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 64;

const RootStyle = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden'
});

const MainStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  paddingTop: APP_BAR_MOBILE + 24,
  paddingBottom: theme.spacing(2),
  [theme.breakpoints.up('lg')]: {
    paddingTop: APP_BAR_DESKTOP + 24,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }
}));

const ContentStyle = styled('div')({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column'
});

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const theme = useTheme();
  const { collapseClick } = useCollapseDrawer();
  const { impersonation, user } = useAuth();
  const [open, setOpen] = useState(false);
  const bannerOffset = impersonation.isImpersonating ? BANNER_HEIGHT : 0;

  // A QR-registered worker who hasn't yet picked who they work for can't use
  // the dashboard. The stashed scanned-site handle is the "still owes a team
  // pick" signal (cleared once they file a join request); while it's set and
  // they belong to no team, send them to the pick-employer step.
  const isWorker =
    user?.role === userRoles.subcontractor || user?.role === userRoles.employee;
  if (isWorker && !user?.team?.ownerUserId && peekOnboardTeamSite()) {
    return <Navigate to="/m/pick-employer" replace />;
  }

  return (
    <RootStyle sx={bannerOffset ? { pt: `${bannerOffset}px` } : undefined}>
      <ImpersonationBanner />
      <DashboardNavbar onOpenSidebar={() => setOpen(true)} />
      <DashboardSidebar isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />
      <MainStyle
        sx={{
          transition: theme.transitions.create('margin', {
            duration: theme.transitions.duration.complex
          }),
          ...(bannerOffset && {
            paddingTop: `${APP_BAR_MOBILE + 24 + bannerOffset}px`,
          }),
          ...(collapseClick && {
            [theme.breakpoints.up('lg')]: {
              ml: '102px',
              '& .MuiContainer-maxWidthLg, & .MuiContainer-maxWidthXl': {
                maxWidth: 'none'
              }
            }
          })
        }}
      >
        <ContentStyle>
          <ReadOnlyBanner />
          <Outlet />
        </ContentStyle>
        <DashboardFooter />
      </MainStyle>
    </RootStyle>
  );
}
